import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Stack,
    Avatar,
    Chip,
    Paper,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { useAuth } from '../hooks/useAuth';
import AdminLayout from './AdminLayout';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';


/**
 * USERS LIST PAGE (ADMIN)
 * Modern UI + works WITH existing Sidebar & Navbar layout
 */

export default function UsersListPage() {
    const { userProfile } = useAuth();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (userProfile?.role !== 'admin') return;

        const fetchUsers = async () => {
            setLoading(true);

            try {
                const snapshot = await getDocs(collection(db, 'users'));

                const usersList = snapshot.docs.map((doc) => ({
                    id: doc.id, // REQUIRED for DataGrid
                    name: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim(),
                    email: doc.data().email || '',
                    mobile: doc.data().mobile || '',
                    role: doc.data().role || 'student',
                }));

                setUsers(usersList);
            } catch (error) {
                console.error('Error fetching users:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [userProfile]);


    const filteredUsers = useMemo(() => {
        return users.filter((u) =>
            `${u.name} ${u.email} ${u.mobile}`
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [users, search]);

    const exportToExcel = () => {
        const data = filteredUsers.map(({ id, ...rest }) => rest);
        const sheet = XLSX.utils.json_to_sheet(data);
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Users');
        XLSX.writeFile(book, 'users.xlsx');
    };

    if (userProfile?.role !== 'admin') {
        return (
            <Box p={4} textAlign="center">
                <Typography color="error" fontWeight={600}>
                    Unauthorized Access
                </Typography>
            </Box>
        );
    }

    const columns = [
        {
            field: 'name',
            headerName: 'User',
            flex: 1.4,
            renderCell: (params) => (
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: '#0D47A1' }}>
                        {params.value.charAt(0)}
                    </Avatar>
                    <Box>
                        <Typography fontWeight={600}>{params.value}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {params.row.email}
                        </Typography>
                    </Box>
                </Stack>
            ),
        },
        { field: 'mobile', headerName: 'Mobile No', flex: 1 },
        {
            field: 'role',
            headerName: 'Role',
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value.toUpperCase()}
                    size="small"
                    color={params.value === 'admin' ? 'error' : 'primary'}
                    variant="outlined"
                />
            ),
        },
    ];

    return (
        <AdminLayout>
            <Box sx={{ p: 3 }}>
                {/* PAGE HEADER */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3,
                        p: 3,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #0D47A1, #1976D2)',
                        color: '#fff',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <PeopleIcon fontSize="large" />
                            <Box>
                                <Typography variant="h5" fontWeight={700}>
                                    Users
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                    Manage all registered users
                                </Typography>
                            </Box>
                        </Stack>

                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<DownloadIcon />}
                            onClick={exportToExcel}
                        >
                            Download Excel
                        </Button>
                    </Stack>
                </Paper>

                {/* CONTENT */}
                <Card sx={{ borderRadius: 4 }}>
                    <Box sx={{ p: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Search users"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                        />
                    </Box>

                    <Box sx={{ height: 520 }}>
                        <DataGrid
                            rows={filteredUsers}
                            columns={columns}
                            loading={loading}
                            disableRowSelectionOnClick
                            pageSizeOptions={[5, 10, 20]}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaders': {
                                    fontWeight: 700,
                                    backgroundColor: '#F5F7FA',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            }}
                        />
                    </Box>
                </Card>
            </Box>
        </AdminLayout>
    );
}
